import { type Block, ContentTypeEnum, type StoryPage } from "@/react/story/types";

/**
 * Préchargement des images d'une page avant qu'elle ne s'affiche.
 *
 * Les stories embarquent des images lourdes (plusieurs Mo pour un fond de page). Le
 * document ne précharge que la première page (`Preloads` côté serveur) ; le viewer
 * demande ici les pages voisines de celle qu'on lit, pour qu'elles soient en cache quand
 * le lecteur glisse vers elles, sans jamais charger toute la story d'un coup.
 */
class PreloadUtilsWrapper {

    private readonly requested = new Set<string>();

    /**
     * Adresses des images qu'une page affiche : fond de page, fonds et séparateurs des
     * deux blocs, images de contenu. Sans doublon, dans l'ordre d'apparition.
     */
    pageImages(page: StoryPage): string[] {
        const uris: string[] = [];
        const push = (uri: string | null | undefined) => {
            if (uri && !uris.includes(uri)) {
                uris.push(uri);
            }
        };
        const block = (b: Block | null) => {
            if (!b) {
                return;
            }
            push(b.config.backgroundImage?.uri);
            push(b.separator?.config.backgroundImage?.uri);
            b.contents.forEach(content => {
                if (content.type === ContentTypeEnum.IMAGE) {
                    push(content.config.image?.uri);
                }
            });
        };

        push(page.config.backgroundImage?.uri);
        block(page.top);
        block(page.bottom);

        return uris;
    }

    /**
     * Demande les images au navigateur, une seule fois par adresse pour la durée de la
     * visite : le cache HTTP fait le reste.
     */
    preload(uris: string[]): void {
        if (typeof Image === "undefined" || this.savesData()) {
            return;
        }
        uris.forEach(uri => {
            if (this.requested.has(uri)) {
                return;
            }
            this.requested.add(uri);
            const image = new Image();
            // Les images de la page affichée passent avant celles qu'on charge d'avance.
            image.fetchPriority = "low";
            image.src = uri;
        });
    }

    /**
     * Se résout quand toutes les images sont arrivées (ou ont échoué), et au plus tard
     * après `maxWait` ms : une image perdue ne doit jamais bloquer ce qui attend la page.
     */
    whenLoaded(uris: string[], maxWait: number): Promise<void> {
        if (uris.length === 0 || typeof Image === "undefined") {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            let pending = uris.length;
            const timer = setTimeout(resolve, maxWait);
            const done = () => {
                pending -= 1;
                if (pending <= 0) {
                    clearTimeout(timer);
                    resolve();
                }
            };
            uris.forEach(uri => {
                const image = new Image();
                image.onload = done;
                image.onerror = done;
                image.src = uri;
            });
        });
    }

    /**
     * Une page qui porte une vidéo ou un son ne doit pas être construite d'avance : son
     * lecteur se chargerait, voire démarrerait, derrière la page qu'on lit.
     */
    hasMedia(page: StoryPage): boolean {
        const carries = (b: Block | null) => !!b && b.contents.some(
            content => content.type === ContentTypeEnum.VIDEO || content.type === ContentTypeEnum.AUDIO,
        );

        return carries(page.top) || carries(page.bottom);
    }

    // Un lecteur qui a demandé à économiser ses données ne charge que ce qu'il regarde.
    savesData(): boolean {
        const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
        return connection?.saveData === true;
    }

    preloadPage(page: StoryPage | null | undefined): void {
        if (page) {
            this.preload(this.pageImages(page));
        }
    }
}

const PreloadUtils = new PreloadUtilsWrapper();

export default PreloadUtils;
